import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { queryKeys } from '@/lib';
import { customAxios } from '@/lib/axios';

export enum Role {
  PCN_ADMIN = 'PCN Admin',
  PCN = 'PCN',
  MA_USER = 'MA User',
  MA_ADMIN = 'MA Admin',
  VERWALTER = 'VERWALTER',
  MA_SUPER_ADMIN = 'MA Super Admin',
  SZ = 'SZ',
  SZ_ADMIN = 'SZ Admin',
  HUB = 'HUB',
  HUB_ADMIN = 'HUB Admin',
  ZUP = 'ZUP',
  ZUP_ADMIN = 'ZUP Admin',
  ZUP_REKLA = 'ZUP Rekla',
  ZUP_KUNDENBETREUER = 'ZUP Kundenbetreuer',
  ZUP_SCANUSER = 'ZUP Scanuser',
  KUNDE = 'KUNDE',
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
}

export interface User {
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
  userId: string;
}

export interface Organization {
  name: string;
  number: string;
  type: string;
}

export interface DecodedToken {
  sub: string;
  role: string;
  user: User;
  organization: Organization;
  exp: number;
  iat: number;
}

export interface UserInfoResponse {
  user: User;
  organization: Organization;
}

export const login = (loginData: LoginRequest) => {
  return customAxios<LoginResponse>({
    url: '/auth/login',
    method: 'POST',
    data: loginData,
  });
};

export const fetchUserInfo = async (): Promise<UserInfoResponse> => {
  return customAxios<UserInfoResponse>({
    url: '/users/me',
    method: 'GET',
  });
};

export const useLogin = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: LoginRequest) => login(data),
    onSuccess: async (data) => {
      localStorage.setItem('accessToken', data.accessToken);
      const redirectUrl = localStorage.getItem('redirectUrl') || '/';
      localStorage.removeItem('redirectUrl');
      await navigate({ to: redirectUrl });
    },
  });
};

export const useUserInfo = () => {
  return useQuery({
    queryKey: queryKeys.userInfo.detail,
    queryFn: fetchUserInfo,
    enabled: isAuthenticated(),
  });
};

export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('accessToken');
};

export const getToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

export const logout = (): void => {
  localStorage.removeItem('accessToken');
};
